import React, { useState, useEffect, useCallback, useRef } from 'react';
import { theme } from '../theme';
import { HEALTH_POLL_MS } from '../timings';
import { smarttix } from '../services/api';
import { HealthResponse, UserRoles } from '../serverTypes';
import { CurrentUser, signOut } from '../auth/identity';
import { CogwheelIcon, PinIcon } from './icons';
import { FirstRunTooltip } from './FirstRunTooltip';
import { useTicketManager } from '../context/TicketManagerContext';

// Spec 2026-05-27 §8: the fixed role vocabulary for the cogwheel badge.
// Compound roles take the highest tier (Admin > Kunden-Admin > Sachbearbeiter).
function formatRoleLabel(roles: UserRoles | null | undefined): string {
  if (!roles) return '';
  if (roles.isAdmin) return 'Smarttix-Administrator';
  if (roles.isCustomerAdmin) return 'Kunden-Administrator';
  if (roles.isNormalUser) return 'Sachbearbeiter';
  return '';
}

export type PageType = 'dashboard' | 'uploads' | 'responses' | 'archive' | 'statistics' | 'chatbot' | 'admin' | 'testgenerator';

interface NavBarProps {
  activePage: PageType;
  onNavigate: (page: PageType) => void;
  user: CurrentUser | null;
  userRoles: UserRoles | null;
  chatbotEnabled?: boolean;
}

// The logo artwork's own height. Equal to the bar's today, and deliberately a SEPARATE number: the
// theme token means "where the navbar's bottom edge is" and overlays lay themselves out against it
// (CompareStage), so sizing artwork from it would let a trim of this image move every overlay in
// the app.
const LOGO_HEIGHT = 56;

// Operator ruling R36.5b (2026-09-28): this nav item names the UPLOADS PAGE, not the served
// `bucket.uploads` word DashboardSeite's own Verlauf tab reads — a tenant renaming that bucket's
// wording must not rename the app's own navigation entry point, so this goes back to the fixed
// literal (fix wave 2026-09-27, item 2, had it read the served word; reverted here). The Dashboard's
// own bucket tab of the same key (DashboardSeite.tsx) keeps reading the served word — it names the
// TAB, not the page.
const CORE_NAV_ITEMS: { page: PageType; label: React.ReactNode }[] = [
  { page: 'dashboard', label: 'Dashboard' },
  { page: 'uploads', label: 'Uploads' },
  { page: 'responses', label: 'Antworten' },
  { page: 'archive', label: 'Archiv' },
];

const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';
const COMPACT_BREAKPOINT = 900;

const NavBar: React.FC<NavBarProps> = ({ activePage, onNavigate, user, userRoles, chatbotEnabled = false }) => {
  const tm = useTicketManager();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [hoveredPage, setHoveredPage] = useState<PageType | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeletedAt, setBulkDeletedAt] = useState<string | null>(null);
  const [compact, setCompact] = useState(typeof window !== 'undefined' && window.innerWidth < COMPACT_BREAKPOINT);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Why: admin pages default hidden in nav, toggle persisted to localStorage
  const [showStatisticsInNav, setShowStatisticsInNav] = useState(() =>
    localStorage.getItem('stx_nav_show_statistics') === 'true'
  );
  const [showChatbotInNav, setShowChatbotInNav] = useState(() =>
    localStorage.getItem('stx_nav_show_chatbot') === 'true'
  );
  const [showAdminInNav, setShowAdminInNav] = useState(() =>
    localStorage.getItem('stx_nav_show_admin') === 'true'
  );
  const [showTestgenInNav, setShowTestgenInNav] = useState(() =>
    localStorage.getItem('stx_nav_show_testgen') === 'true'
  );

  const settingsRef = useRef<HTMLDivElement>(null);

  const isAdmin = !!(userRoles?.isAdmin || userRoles?.isCustomerAdmin);
  const isSuperAdmin = !!userRoles?.isAdmin;
  const isDev = skipAuth;

  const navItems = [
    ...CORE_NAV_ITEMS,
    ...(isAdmin && showStatisticsInNav ? [{ page: 'statistics' as PageType, label: 'Statistik' }] : []),
    ...(isAdmin && chatbotEnabled && showChatbotInNav ? [{ page: 'chatbot' as PageType, label: 'Chatbot' }] : []),
    // Einstellungen pin: ungated per Spec §4 — any authenticated user can pin it
    // since the page itself is reachable by every role (per-tab gating inside).
    ...(userRoles && showAdminInNav ? [{ page: 'admin' as PageType, label: 'Einstellungen' }] : []),
    ...(isSuperAdmin && showTestgenInNav ? [{ page: 'testgenerator' as PageType, label: 'Testgenerator' }] : []),
  ];

  const userName = user?.name || user?.email || '';
  const displayName = skipAuth ? 'Smarttix Entwickler' : (userName || 'Gast');

  const toggleStatisticsInNav = () => {
    const next = !showStatisticsInNav;
    setShowStatisticsInNav(next);
    localStorage.setItem('stx_nav_show_statistics', String(next));
  };

  const toggleChatbotInNav = () => {
    const next = !showChatbotInNav;
    setShowChatbotInNav(next);
    localStorage.setItem('stx_nav_show_chatbot', String(next));
  };

  const toggleAdminInNav = () => {
    const next = !showAdminInNav;
    setShowAdminInNav(next);
    localStorage.setItem('stx_nav_show_admin', String(next));
  };

  const toggleTestgenInNav = () => {
    const next = !showTestgenInNav;
    setShowTestgenInNav(next);
    localStorage.setItem('stx_nav_show_testgen', String(next));
  };

  const pollHealth = useCallback(async () => {
    try {
      const h = await smarttix.checkHealth();
      setHealth(h);
    } catch {
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    pollHealth();
    const interval = setInterval(pollHealth, HEALTH_POLL_MS);
    return () => clearInterval(interval);
  }, [pollHealth]);

  useEffect(() => {
    const onResize = () => {
      const isCompact = window.innerWidth < COMPACT_BREAKPOINT;
      setCompact(isCompact);
      if (!isCompact) setHamburgerOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Why: close settings dropdown when clicking outside
  useEffect(() => {
    if (!settingsOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [settingsOpen]);

  const healthColor = health === null ? theme.colors.textSecondary
    : health.status === 'healthy' ? theme.colors.success
      : health.status === 'degraded' ? theme.colors.warning
        : theme.colors.error;

  const healthLabel = health === null ? 'Nicht erreichbar'
    : health.status === 'healthy' ? 'Betriebsbereit'
      : health.status === 'degraded' ? 'Eingeschränkt'
        : 'Beeinträchtigt';

  // Through the facade, never a logout URL: the session this app holds is the MSAL account cache,
  // and a platform logout link would leave it intact — Abmelden plus a reload would then silently
  // sign the same user back in. msalAuth owns the redirect and the post-logout landing.
  const handleLogout = () => { void signOut(); };

  const handleBulkDelete = async () => {
    // Why: DEV-tool but still destructive. Plausibility confirm per DGUV Ch.9 —
    // operator should not be able to fat-finger "Alle löschen" without a prompt
    // that quotes the action + reversibility window.
    // eslint-disable-next-line no-restricted-globals
    const ok = window.confirm(
      'Alle Tickets dieses Kunden löschen?\n\nDiese Aktion ist 30 Sekunden lang über „Rückgängig" im Menü zu widerrufen, danach endgültig.'
    );
    if (!ok) return;
    setBulkDeleting(true);
    try {
      // Routed through the tm chokepoint (not a direct smarttix call) so the announce that
      // every other mutator fires happens here too — any other open tab/window hears about
      // it instead of waiting out its own next poll tick.
      const result = await tm.bulkDeleteTickets();
      if (result.deletedAt) {
        setBulkDeletedAt(result.deletedAt);
      }
    } catch { /* silent */ }
    setBulkDeleting(false);
  };

  const handleUndoBulkDelete = async () => {
    if (!bulkDeletedAt) return;
    try {
      await tm.undoBulkDelete(bulkDeletedAt);
      setBulkDeletedAt(null);
    } catch { /* silent */ }
  };

  const handleNavigate = (page: PageType) => {
    onNavigate(page);
    setHamburgerOpen(false);
    setSettingsOpen(false);
  };

  // ─── Health dot (tooltip-only) ────────────────────────────

  const healthDot = (
    <div title={healthLabel} style={{
      display: 'flex', alignItems: 'center', cursor: 'default',
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: '50%',
        backgroundColor: healthColor,
        boxShadow: health?.status === 'healthy' ? `0 0 6px ${theme.colors.success}` : 'none',
        transition: 'background-color 0.3s ease',
      }} />
    </div>
  );

  // ─── Settings dropdown ────────────────────────────────────

  const settingsDropdown = settingsOpen && (
    <div style={{
      position: 'absolute', top: '100%', right: 0,
      marginTop: 8, width: 'max-content', minWidth: 200,
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.md,
      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
      border: `1px solid ${theme.colors.borderLight}`,
      zIndex: 1001, overflow: 'hidden',
      animation: 'stx-slideIn 0.15s ease',
      fontFamily: theme.font.family,
    }}>
      {/* User info header */}
      <div style={{
        padding: `${theme.spacing.md}px ${theme.spacing.md}px ${theme.spacing.sm}px`,
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        whiteSpace: 'nowrap',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.navy, lineHeight: 1.3 }}>
          {displayName}
        </div>
        {user?.email && (
          <div style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.3, marginTop: 2 }}>
            {user.email}
          </div>
        )}
        {userRoles && formatRoleLabel(userRoles) && (
          <div style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.3, marginTop: 4 }}>
            Angemeldet als: {formatRoleLabel(userRoles)}
          </div>
        )}
        {isDev && (
          <div style={{ fontSize: 10, color: theme.colors.warning, marginTop: 4, fontWeight: 500 }}>
            Entwicklermodus
          </div>
        )}
      </div>

      {/* Admin pages — single row per page: clickable label + nav toggle.
          Einstellungen is now reachable by every authenticated user (Spec §4 — per-tab
          role gating inside the page does the rest). Statistik + Chatbot stay
          isAdmin-gated (those pages have no Sachbearbeiter affordance). */}
      {userRoles && (
        <div style={{
          padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
          borderBottom: `1px solid ${theme.colors.borderLight}`,
        }}>
          {isAdmin && (
            <AdminPageRow
              label="Statistik"
              active={activePage === 'statistics'}
              pinned={showStatisticsInNav}
              onNavigate={() => handleNavigate('statistics')}
              onTogglePin={toggleStatisticsInNav}
            />
          )}
          {chatbotEnabled && isAdmin && (
            <AdminPageRow
              label="Chatbot"
              active={activePage === 'chatbot'}
              pinned={showChatbotInNav}
              onNavigate={() => handleNavigate('chatbot')}
              onTogglePin={toggleChatbotInNav}
            />
          )}
          <AdminPageRow
            label="Einstellungen"
            active={activePage === 'admin'}
            pinned={showAdminInNav}
            onNavigate={() => handleNavigate('admin')}
            onTogglePin={toggleAdminInNav}
          />
          {isSuperAdmin && (
            <AdminPageRow
              label="Testgenerator"
              active={activePage === 'testgenerator'}
              pinned={showTestgenInNav}
              onNavigate={() => handleNavigate('testgenerator')}
              onTogglePin={toggleTestgenInNav}
            />
          )}
        </div>
      )}

      {/* Dev tools */}
      {isDev && (
        <div style={{
          padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
          borderBottom: `1px solid ${theme.colors.borderLight}`,
        }}>
          <MenuButton
            label={bulkDeleting ? 'Lösche...' : 'Alle löschen'}
            onClick={handleBulkDelete}
            disabled={bulkDeleting}
            danger
          />
          {bulkDeletedAt && (
            <MenuButton label="Rückgängig" onClick={handleUndoBulkDelete} />
          )}
        </div>
      )}

      {/* Logout */}
      {!skipAuth && user && (
        <div style={{ padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}>
          <MenuButton label="Abmelden" onClick={handleLogout} />
        </div>
      )}
    </div>
  );

  // ─── Render ───────────────────────────────────────────────

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 1000,
      backgroundColor: theme.colors.navy,
      fontFamily: theme.font.family,
    }}>
      {/* Main bar */}
      {/* The bar's height is a THEME token (Part C walk round, 2026-09-09), not a literal: this bar
          is sticky at z-index 1000, above every overlay in the app, so a viewport-positioned
          overlay has to lay itself out below its bottom edge — CompareStage reads the same token
          for exactly that. Two spellings of 56 would let the overlay drift under the bar silently. */}
      <div style={{
        display: 'flex', alignItems: 'stretch',
        padding: `0 ${theme.spacing.lg}px`, height: theme.layout.navBarHeight,
      }}>
        {/* Logo — its own constant (round-2 review, 2026-09-09). It happens to equal the bar's
            height today, but the shared token means ONE thing — where the bar's bottom edge is, so
            an overlay can lay itself out below it (CompareStage reads it for exactly that). Sizing
            the artwork from that token conflated the two: a logo trimmed to sit inside the bar
            would have silently moved every overlay in the app. */}
        <img
          src="/smarttix-logo.svg"
          alt="Smarttix"
          style={{ height: LOGO_HEIGHT, marginRight: theme.spacing.xl, alignSelf: 'center' }}
        />

        {!compact && (
          <>
            {/* Nav links */}
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 2, flex: 1, justifyContent: 'center' }}>
              {navItems.map((item) => {
                const isActive = activePage === item.page;
                const isHovered = hoveredPage === item.page;
                return (
                  <React.Fragment key={item.page}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <button
                        onClick={() => handleNavigate(item.page)}
                        onMouseEnter={() => setHoveredPage(item.page)}
                        onMouseLeave={() => setHoveredPage(null)}
                        style={{
                          padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                          fontSize: 14,
                          fontWeight: isActive ? 600 : 400,
                          fontFamily: theme.font.family,
                          color: isActive ? theme.colors.white
                            : isHovered ? theme.colors.bgLight
                              : theme.colors.textOnDark,
                          backgroundColor: 'transparent',
                          border: 'none', cursor: 'pointer',
                          transition: 'all 0.15s ease', whiteSpace: 'nowrap',
                        }}
                      >
                        {item.label}
                      </button>
                      {isActive && (
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: 2, backgroundColor: theme.colors.white,
                        }} />
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Right side: health dot + settings */}
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, alignSelf: 'center' }}>
              {healthDot}
              <FirstRunTooltip
                storageKey="navbar-pin-v1"
                title="Häufige Seiten festpinnen"
                body="Über das Zahnrad lassen sich Admin-Seiten (Statistik, Einstellungen, …) dauerhaft in die obere Navigation pinnen."
                placement="below"
              >
              <div ref={settingsRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setSettingsOpen(o => !o)}
                  style={{
                    width: 40, height: 40,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: settingsOpen ? theme.colors.navyLight : 'transparent',
                    border: 'none', borderRadius: theme.borderRadius.sm,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  aria-label="Einstellungen"
                >
                  <CogwheelIcon size={18} color={theme.colors.textOnDark} />
                </button>
                {settingsDropdown}
              </div>
              </FirstRunTooltip>
            </div>
          </>
        )}

        {compact && (
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: theme.spacing.sm }}>
            {healthDot}
            <div ref={!hamburgerOpen ? settingsRef : undefined} style={{ position: 'relative' }}>
              <button
                onClick={() => { setSettingsOpen(o => !o); setHamburgerOpen(false); }}
                style={{
                  width: 40, height: 40,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: settingsOpen ? theme.colors.navyLight : 'transparent',
                  border: 'none', borderRadius: theme.borderRadius.sm,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
                aria-label="Einstellungen"
              >
                <CogwheelIcon size={18} color={theme.colors.textOnDark} />
              </button>
              {settingsDropdown}
            </div>
            <button
              onClick={() => { setHamburgerOpen(o => !o); setSettingsOpen(false); }}
              style={{
                width: 40, height: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 0,
                fontSize: 22, lineHeight: 1,
                color: theme.colors.white,
                backgroundColor: hamburgerOpen ? theme.colors.navyLight : 'transparent',
                border: 'none', cursor: 'pointer',
                borderRadius: theme.borderRadius.sm,
                transition: 'background-color 0.15s ease',
              }}
              aria-label="Menü"
            >
              {hamburgerOpen ? '\u2715' : '\u2630'}
            </button>
          </div>
        )}
      </div>

      {/* Compact dropdown menu — nav links only */}
      {compact && hamburgerOpen && (
        <div style={{
          backgroundColor: theme.colors.navyDark,
          borderTop: `1px solid ${theme.colors.navyLight}`,
          padding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
          display: 'flex', flexDirection: 'column', gap: theme.spacing.sm,
          animation: 'stx-slideIn 0.2s ease',
        }}>
          {navItems.map((item) => {
            const isActive = activePage === item.page;
            return (
              <button
                key={item.page}
                onClick={() => handleNavigate(item.page)}
                style={{
                  padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 400,
                  fontFamily: theme.font.family,
                  color: isActive ? theme.colors.white : theme.colors.textOnDark,
                  backgroundColor: isActive ? theme.colors.navyLight : 'transparent',
                  border: 'none', cursor: 'pointer',
                  borderRadius: theme.borderRadius.sm,
                  textAlign: 'left',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </nav>
  );
};

// ─── Sub-components ───────────────────────────────────────

const AdminPageRow: React.FC<{
  label: string; active: boolean; pinned: boolean;
  onNavigate: () => void; onTogglePin: () => void;
}> = ({ label, active, pinned, onNavigate, onTogglePin }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `5px 0`, gap: theme.spacing.md,
  }}>
    <button
      onClick={onNavigate}
      title={label}
      style={{
        flex: 1, textAlign: 'left', padding: 0,
        fontSize: 12, fontFamily: theme.font.family,
        fontWeight: active ? 600 : 400,
        color: active ? theme.colors.navy : theme.colors.navyMid,
        backgroundColor: 'transparent', border: 'none',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
    <button
      onClick={e => { e.stopPropagation(); onTogglePin(); }}
      title={pinned ? 'Aus Navigationsleiste entfernen' : 'In Navigationsleiste anzeigen'}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, flexShrink: 0,
        backgroundColor: 'transparent', border: 'none',
        cursor: 'pointer', borderRadius: theme.borderRadius.sm,
        opacity: pinned ? 1 : 0.4,
        transition: 'opacity 0.15s ease',
      }}
    >
      <PinIcon size={14} color={theme.colors.navy} filled={pinned} />
    </button>
  </div>
);

const MenuButton: React.FC<{
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; active?: boolean;
}> = ({ label, onClick, disabled, danger, active }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'block', width: '100%', textAlign: 'left',
      padding: `5px 0`, fontSize: 12,
      fontFamily: theme.font.family, fontWeight: active ? 600 : 400,
      color: danger ? theme.colors.error : active ? theme.colors.navy : theme.colors.navyMid,
      backgroundColor: 'transparent', border: 'none',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    {label}
  </button>
);

export default NavBar;
